<script setup lang="ts">
import { computed, ref } from "vue"
import { useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import { useEpciCommuneStore, useOnlineStore } from "../../store"
import OfflineChip from "../Offline/Chip.vue"
import { useNuxtApp } from "#app"

// Déclaration des props
defineProps({
    rail: { type: Boolean, required: true },
})

// Hooks et plugins
const route = useRoute()
const { $permissionCheck, $currentUser } = useNuxtApp()

// Stores
const epciCommuneStore = useEpciCommuneStore()
const onlineStore = useOnlineStore()

const { currentEpciCommune } = storeToRefs(epciCommuneStore)
const { online } = storeToRefs(onlineStore)

// Variables réactives
const hover = ref(false)

// Propriétés calculées (Computed)
const isLoggedIn = computed(() => !!$currentUser()?.k)
const isAdmin = computed(() => ['admin'].includes($currentUser()?.role))
const isSuperUser = computed(() => ['superuser'].includes($currentUser()?.role))
const hasCurrentCommune = computed(() => !!currentEpciCommune.value?.id)
</script>

<template>
    <div @mouseover="hover = true" @mouseleave="hover = false">
        <v-list-item nav :height="!rail || hover ? undefined : 64">
            <div class="w-100 text-center">
                <img v-if="!rail || hover" src="@/assets/images/logo.png" alt="Logo Larchiviste" width="100">
                <img v-else src="@/assets/images/logo.png" alt="Logo Larchiviste" width="30" class="py-2">
            </div>
        </v-list-item>

        <v-divider />

        <v-list nav color="primary" active-class="bg-light">
            <v-list-item
                v-if="isLoggedIn && ($permissionCheck('dossier_page') || $permissionCheck('epciCommune_page'))"
                :active="route.path === '/'"
                :nuxt="true"
                :disabled="!online"
                to="/"
                value="home"
                :prepend-icon="(isAdmin && !hasCurrentCommune) ? 'mdi-chart-pie' : 'mdi-file-multiple'"
            >
                <v-list-item-title>{{ $translate((isAdmin && !hasCurrentCommune) ? 'statistiques' : 'dossiers') }}</v-list-item-title>

                <template v-if="hasCurrentCommune" #append>
                    <OfflineChip variant="small" />
                </template>
            </v-list-item>

            <div>
                <v-list-item
                    v-if="$permissionCheck('box_page') && currentEpciCommune?.id"
                    :active="route.path.startsWith('/boites')"
                    :nuxt="true"
                    :disabled="!online"
                    to="/boites"
                    prepend-icon="mdi-archive"
                    value="boxes"
                >
                    <v-list-item-title>Boites</v-list-item-title>
                </v-list-item>

                <v-list-item
                    v-if="$permissionCheck('procedure_page') && currentEpciCommune?.id"
                    :active="route.path.startsWith('/procedure')"
                    :nuxt="true"
                    :disabled="!online"
                    to="/procedure"
                    prepend-icon="mdi-application-import"
                    value="procedure"
                >
                    <v-list-item-title>Procédures</v-list-item-title>
                </v-list-item>

                <v-list-item
                    v-if="$permissionCheck('releasedProcedure_page') && hasCurrentCommune"
                    :active="route.path.startsWith('/fiche-de-suivi')"
                    :nuxt="true"
                    :disabled="!online"
                    to="/fiche-de-suivi"
                    prepend-icon="mdi-ghost-outline"
                    value="released-procedure"
                >
                    <v-list-item-title>Fiches de suivi</v-list-item-title>
                </v-list-item>

                <template v-if="isLoggedIn && (isAdmin || isSuperUser)">
                    <v-divider class="my-2" v-if="hasCurrentCommune" />
                    <v-list-item
                        v-if="isLoggedIn && $permissionCheck('epciCommune_page') && isAdmin"
                        :active="route.path === '/epci-communes'"
                        :nuxt="true"
                        :disabled="!online"
                        to="/epci-communes"
                        value="epci-communes"
                        prepend-icon="mdi-city-variant"
                    >
                        <v-list-item-title>Communes et EPCI</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="$permissionCheck('mission_page') && isAdmin"
                        :active="route.path.startsWith('/missions')"
                        :nuxt="true"
                        :disabled="!online"
                        to="/missions"
                        prepend-icon="mdi-town-hall"
                        value="missions"
                    >
                        <v-list-item-title>Missions</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="$permissionCheck('user_page')"
                        :active="route.path.startsWith('/utilisateurs')"
                        :nuxt="true"
                        :disabled="!online"
                        to="/utilisateurs"
                        prepend-icon="mdi-account-group"
                        value="users"
                    >
                        <v-list-item-title>Utilisateurs</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="isLoggedIn && hasCurrentCommune"
                        :nuxt="true"
                        :disabled="!online"
                        prepend-icon="mdi-chart-pie"
                        value="stats"
                        to="/statistiques"
                    >
                        <v-list-item-title>Statistiques</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-if="isLoggedIn && isAdmin"
                        :active="route.path.startsWith('/parametres')"
                        :nuxt="true"
                        :disabled="!online"
                        to="/parametres"
                        prepend-icon="mdi-application-import"
                        value="parametres"
                    >
                        <v-list-item-title>Paramètres</v-list-item-title>
                    </v-list-item>
                </template>
            </div>
        </v-list>
    </div>
</template>

<style scoped>
    /* Target the title directly inside the list */
:deep(.v-list-item-title) {
    font-size: 1rem !important; /* Adjust to your liking */
}
</style>
